import React, { useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { GlgDisabledElementWrapper } from 'Shared/components'

import LeaguesBrowserItem from './LeaguesBrowserItem'
import LeaguesBrowserPagination from './LeaguesBrowserPagination'
import LeaguesBrowserLegend from './LeaguesBrowserLegend'
import { setSelectedLeaguesForCloning, selectSelectedLeaguesForCloning } from '../../reducers/selected_leagues_for_cloning'
import {
  selectPotentialLeaguesForCloning,
  selectPotentialLeaguesForCloningLoading,
  selectPotentialLeaguesForCloningPagination,
} from '../../reducers/potential_leagues_for_cloning'

import { MAX_ALLOWED_SELECTED_LEAGUES } from '../../constants'
import { GlgLoadingOverlay } from '../../../shared/components'

const getLeagueIDsSet = (leaguesList) => {
  return new Set(leaguesList.map(league => league.id))
}

const LeaguesBrowserList = () => {
  const dispatch = useDispatch()
  const isLoading = useSelector(selectPotentialLeaguesForCloningLoading)
  const pagination = useSelector(selectPotentialLeaguesForCloningPagination)
  const pagedLeagues = useSelector(selectPotentialLeaguesForCloning)
  const selectedLeagues = useSelector(selectSelectedLeaguesForCloning)

  // Split into two columns
  const leftColumn = pagedLeagues.slice(0, 10)
  const rightColumn = pagedLeagues.slice(10, 20)

  const isWholePageSelected = useMemo(() => {
    return pagedLeagues.length && getLeagueIDsSet(pagedLeagues).isSubsetOf(getLeagueIDsSet(selectedLeagues))
  }, [ pagedLeagues, selectedLeagues ])

  const removeLeaguesFromSelected = useCallback((leaguesList) => {
    const leaguesToBeRemovedIDs = getLeagueIDsSet(leaguesList)
    const remainingLeagues = selectedLeagues.filter(league => !leaguesToBeRemovedIDs.has(league.id))
    dispatch(setSelectedLeaguesForCloning(remainingLeagues))
  }, [ dispatch, selectedLeagues ])

  const getNewSelectedLeagues = useCallback((leaguesList) => {
    const existingLeagueIDs = getLeagueIDsSet(selectedLeagues)
    const leaguesToBeAdded = leaguesList.filter(league => !existingLeagueIDs.has(league.id))

    return [ ...selectedLeagues, ...leaguesToBeAdded ]
  }, [ selectedLeagues ])


  const addLeaguesToSelected = useCallback((leaguesList) => {
    const newSelectedLeagues = getNewSelectedLeagues(leaguesList)

    dispatch(setSelectedLeaguesForCloning(newSelectedLeagues))
  }, [ dispatch, selectedLeagues ])

  const handleCheckboxChange = useCallback((league) => {
    if (!getLeagueIDsSet(selectedLeagues).has(league.id)) {
      addLeaguesToSelected([ league ])
    } else {
      removeLeaguesFromSelected([ league ])
    }
  }, [ selectedLeagues, addLeaguesToSelected, removeLeaguesFromSelected ])

  const maxLeaguesHaveBeenSelected = React.useMemo(() => {
    return selectedLeagues.length >= MAX_ALLOWED_SELECTED_LEAGUES
  }, [ selectedLeagues ])

  const shouldDisableSelectAll =
    maxLeaguesHaveBeenSelected ||
    getNewSelectedLeagues(pagedLeagues).length > MAX_ALLOWED_SELECTED_LEAGUES

  return (
    <GlgLoadingOverlay isLoading={isLoading} >
      <div className="cls-leagues-browser-list">
        {/* Search Section */}
        <div className="cls-view-header-container">
          <h2 className="cls-lb-search-title">
            <span className="cls-view-panel-text">
              Available to Clone
            </span>
            <span className="cls-lb-counter-text">
              {pagination?.totalCount}
            </span>
          </h2>
        </div>

        {/* Select / Deselect all button*/}
        {isWholePageSelected ?
          <div>
            <button
              onClick={() => removeLeaguesFromSelected(pagedLeagues)}
              className="cls-controls-text"
            >
              {window.I18n.t('buttons.unselect_all')}
            </button>
          </div>
            :
          <GlgDisabledElementWrapper
            disabled={shouldDisableSelectAll}
          >
            <button
              onClick={() => addLeaguesToSelected(pagedLeagues)}
              className="cls-controls-text"
            >
              {window.I18n.t('buttons.select_all')}
            </button>
          </GlgDisabledElementWrapper>
        }

        {/* Two Column Layout */}
        <div className="cls-lb-columns-container">
          {[ leftColumn, rightColumn ].map((columnData, index) => (
            <div key={index} className="cls-lb-column">
              {columnData.map((league) => {
                const isElementSelected = getLeagueIDsSet(selectedLeagues).has(league.id)
                return (
                  <GlgDisabledElementWrapper
                    key={league.id}
                    disabled={maxLeaguesHaveBeenSelected && !isElementSelected}
                  >
                    <LeaguesBrowserItem
                      item={league}
                      onCheckChanged={() => handleCheckboxChange(league)}
                      isSelected={isElementSelected}
                    />
                  </GlgDisabledElementWrapper>
                )
              },
              )}
            </div>
          ))}
        </div>
        <div className="cls-lb-footer">
          <LeaguesBrowserLegend />
          <LeaguesBrowserPagination />
        </div>
      </div>
    </GlgLoadingOverlay>
  )
}

export default LeaguesBrowserList
